import {
  EmailVerificationCommandResult,
  getCutoffsAdvisorOverviewsPdfs,
} from "Api/Api";
import { createAsyncAction, getType } from "typesafe-actions";
import { call, put, takeLatest } from "typed-redux-saga";
import { createSlice } from "@reduxjs/toolkit";
import { mapAPIErrorResponse } from "Models/Errors/ApiCallError";
import {
  getFetchStateDefaultValue,
  handleFailureCase,
  handleRequestCase,
  handleSuccessCase,
} from "State/Utils";
import { downloadBase64File } from "Utils/FileUtils";
import { Resources, getTranslation } from "Translations/Resources";
import { toast } from "sonner";

export const getAllCutoffAdvisorOverviewsPdfsAsync = createAsyncAction(
  "@cutoff/GET_ALL_ADVISOR_OVERVIEWS_PDFS_REQUEST",
  "@cutoff/GET_ALL_ADVISOR_OVERVIEWS_PDFS_RESPONSE",
  "@cutoff/GET_ALL_ADVISOR_OVERVIEWS_PDFS_FAILURE",
)<number, void, Error>();

function* getAllCutoffAdvisorOverviewsPdfs(
  action: ReturnType<typeof getAllCutoffAdvisorOverviewsPdfsAsync.request>,
): Generator {
  try {
    const year = action.payload;
    const { data, error, status } = yield* call(
      getCutoffsAdvisorOverviewsPdfs,
      year,
    );

    if (status === 200) {
      downloadBase64File(
        data.pdfsZipBase64,
        "application/zip",
        `${getTranslation(
          Resources.Settings.Cutoff.AdvisorOverview.DownloadAll.File,
        )}_${year}.zip`,
      );
      yield put(getAllCutoffAdvisorOverviewsPdfsAsync.success());
      return;
    }

    toast.error(
      getTranslation(
        Resources.Settings.Cutoff.AdvisorOverview.DownloadAll.Error,
      ),
    );
    yield put(
      getAllCutoffAdvisorOverviewsPdfsAsync.failure(
        mapAPIErrorResponse(error ?? data),
      ),
    );
  } catch (err) {
    toast.error(
      getTranslation(
        Resources.Settings.Cutoff.AdvisorOverview.DownloadAll.Error,
      ),
    );
    yield put(getAllCutoffAdvisorOverviewsPdfsAsync.failure(err as Error));
  }
}

export function* getAllCutoffAdvisorOverviewsPdfsSaga() {
  yield takeLatest(
    getType(getAllCutoffAdvisorOverviewsPdfsAsync.request),
    getAllCutoffAdvisorOverviewsPdfs,
  );
}

export const getAllCutoffAdvisorOverviewsPdfsSlice = createSlice({
  initialState:
    getFetchStateDefaultValue<EmailVerificationCommandResult>(false),
  name: "@cutoff/getAllCutoffAdvisorOverviewsPdfs",
  reducers: {
    resetSlice: () =>
      getFetchStateDefaultValue<EmailVerificationCommandResult>(false),
  },
  extraReducers: builder => {
    handleRequestCase(getAllCutoffAdvisorOverviewsPdfsAsync, builder);
    handleSuccessCase(getAllCutoffAdvisorOverviewsPdfsAsync, builder);
    handleFailureCase(getAllCutoffAdvisorOverviewsPdfsAsync, builder);
  },
});

export const { resetSlice } = getAllCutoffAdvisorOverviewsPdfsSlice.actions;
